/* eslint-disable max-lines-per-function */
import React, { useCallback, useEffect } from 'react'
import { useLang } from '~/hooks/useLang'
import useDispatchRegistrationState from '~/features/Dispatch/stores/DispatchRegistrationState'
import useDispatchState from '~/features/Dispatch/stores/DispatchState'
import { FormControl, Grid, TextField } from '@mui/material'
import { Downgraded, State, useHookstate } from '@hookstate/core'
import useDispatchRegistrationAnimalState from '~/features/Dispatch/stores/DispatchRegistrationAnimalState'
import FieldAutocompleteRegistration from '~/components/FieldAutocompleteRegistration'
import Buttons from '../../../DispatchRegistrationTab/components/Buttons'
import notify from '~/utils/notify'
import FilterAutocompleteFetch from '~/components/FilterAutocompleteFetch'
import FieldTextRegistration from '~/components/FieldTextRegistration'

interface IFormAnimals {
  readonly loading: State<boolean>
  readonly handleEdit: () => void
  readonly handleClickOpenConfirmAction: (action: string) => void
}

export default function FormAnimal({
  handleEdit,
  loading,
  handleClickOpenConfirmAction,
}: IFormAnimals) {
  const { translate } = useLang()
  const resetValeus = useHookstate(false)

  const { dispatch } = useDispatchState()
  const { occurrence, status } = dispatch()
  const { id: occurrenceIdState } = occurrence

  const disableFields = status.value.id === 8

  const { registrationAnimalDispatch } = useDispatchRegistrationAnimalState()
  const { animal } = registrationAnimalDispatch()

  const { registrationDispatch, getCircunstances, getDestinations } =
    useDispatchRegistrationState()
  const { getBreeds, sendAnimal } = useDispatchRegistrationAnimalState()

  const { inputErrors, addRegistration } = registrationDispatch()

  const someInvalidInput = () => {
    if (!animal.get().specieId) {
      inputErrors.specieId.set(true)
    }
    if (!animal.get().animalCircunstance) {
      inputErrors.animalCircunstance.set(true)
    }
    if (!animal.get().destination) {
      inputErrors.destination.set(true)
    }

    return Object.values(inputErrors.attach(Downgraded).get()).some((value) => !!value)
  }

  useEffect(() => {
    animal.set((prev) => ({ ...prev, occurrenceId: occurrenceIdState.get() }))
  }, [])

  const handleSend = useCallback(async () => {
    if (someInvalidInput()) {
      return
    }
    loading.set(true)
    await sendAnimal(animal.get()).finally(() => {
      loading.set(false)
    })
    notify({ message: translate('Successfully created'), type: 'success' })
    addRegistration.set(false)
  }, [addRegistration, sendAnimal, translate, animal])


  return (
    <FormControl fullWidth size="small">
      <Grid container direction={'row'}>
        <Grid item xs={3} sx={{ padding: 1 }}>
          <FilterAutocompleteFetch
            label={'Specie'}
            model="specie"
            isRequired
            urlFetch={'/specie/get-by-name'}
            size={'small'}
            disabled={disableFields}
            onFetchChange={(specie: number | null) =>
              animal.merge({
                specieId: specie,
              })
            }
            valueId={Number(animal.get()?.specieId)}
            resetValues={resetValeus.get()}
            dataRequest="name"
            error={inputErrors.specieId.get()}
            onError={(value) => inputErrors.specieId.set(value)}
          />
        </Grid>
        <Grid item xs={3} sx={{ padding: 1 }}>
          <FieldAutocompleteRegistration
            valueId={animal.get()?.breedId as string}
            depends={animal.get()?.specieId as string}
            label={translate('Breed')}
            onValueChange={(kind) => {
              animal.breedId.set(kind?.id as string)
            }}
            optionsFetch={getBreeds}
            disabled={disableFields || !animal.get()?.specieId}
          />
        </Grid>

        <Grid item xs={3} sx={{ padding: 1 }}>
          <FieldAutocompleteRegistration
            label={translate('Circunstance')}
            onValueChange={(animalCircunstance) =>
              animal.animalCircunstance.set({
                id: animalCircunstance?.id ?? '',
                name: animalCircunstance?.name ?? '',
              })
            }
            valueId={animal?.animalCircunstance.get()?.id as string}
            optionsFetch={getCircunstances}
            isRequired
            disabled={disableFields}
            error={inputErrors.animalCircunstance.get()}
            onError={(value) => inputErrors.animalCircunstance.set(value)}
          />
        </Grid>
        <Grid item xs={3} sx={{ padding: 1 }}>
          <FieldAutocompleteRegistration
            label={translate('Destination')}
            onValueChange={(destination) =>
              animal.destination.set({
                id: destination?.id ?? '',
                name: destination?.name ?? '',
              })
            }
            valueId={animal?.destination?.get()?.id as string}
            optionsFetch={getDestinations}
            isRequired
            disabled={disableFields}
            error={inputErrors.destination.get()}
            onError={(value) => inputErrors.destination.set(value)}
          />
        </Grid>
        <Grid item xs={12} sx={{ padding: 1, marginBottom: 1 }}>
          <FieldTextRegistration
            disableFields={disableFields}
            label={'Note'}
            value={animal.notes.get()}
            onChangeValue={(value) => animal.notes.set(value)}
            multiline={true}
            rows={2}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Buttons
          loading={loading}
          isUpdate={!!animal.id.get()}
          handleClickOpenConfirmAction={handleClickOpenConfirmAction}
          handleEdit={handleEdit}
          handleSend={handleSend}
          someInvalidInput={someInvalidInput}
          disableFields={disableFields}
        />
      </Grid>
    </FormControl>
  )
}
